import React, { useState, useEffect } from 'react';
import { X, CreditCard, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Stripe } from '@stripe/stripe-js';
import { trackSubscription, trackError } from '../Utils/analytics';
import StripePaymentForm from '../StripePaymentForm/StripePaymentForm';
import '../PaymentModal/PaymentModal.css';

interface PaymentModalProps {
  onClose: () => void;
  onSuccess: () => void;
  selectedPlan?: string;
  stripePromise: Promise<Stripe | null>;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ onClose, onSuccess, selectedPlan, stripePromise }) => {
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showBankTransfer, setShowBankTransfer] = useState(false);
  const [paypalOrderData, setPaypalOrderData] = useState<any>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'select' | 'paypal' | 'stripe' | 'bank'>('select');
  const navigate = useNavigate();

  // Plan pricing mapping - matches API plan_type values
  const planPricing = {
    weekly: { amount: '3.99', currency: 'GBP', interval: 'week', planType: 'WEEKLY' },
    monthly: { amount: '4.99', currency: 'GBP', interval: 'month', planType: 'MONTHLY' },
    yearly: { amount: '49.99', currency: 'GBP', interval: 'year', planType: 'YEARLY' }
  };

  const currentPlan = selectedPlan && planPricing[selectedPlan as keyof typeof planPricing] 
    ? planPricing[selectedPlan as keyof typeof planPricing] 
    : planPricing.monthly;

  // Check for active subscription when modal opens
  useEffect(() => {
    const checkActiveSubscription = async () => {
      try {
        const authToken = localStorage.getItem('authToken');
        
        if (!authToken) {
          return;
        }

        const response = await fetch('https://api.homecookt.com/api/v1/paypal/current-subscription', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        });

        if (response.ok) {
          const subscriptionData = await response.json();
          if (subscriptionData.status === 'COMPLETED') {
            setHasActiveSubscription(true);
            setError('You already have an active subscription. Please contact support to change your plan.');
            trackError('active_subscription_detected', 'payment_modal');
          }
        }
      } catch (error) {
        console.log('Could not check subscription status');
        trackError('subscription_check_failed', 'payment_modal');
      }
    };

    checkActiveSubscription();
  }, []);

  const createPayPalOrder = async () => {
    // Block payment if user has active subscription
    if (hasActiveSubscription) {
      setError('You already have an active subscription. Please contact support to change your plan.');
      trackError('duplicate_subscription_blocked', 'payment_modal');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        setError('Please log in to continue with payment');
        setIsProcessing(false);
        trackError('payment_no_auth', 'payment_modal');
        return;
      }

      // Determine the correct domain for return URLs
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const currentDomain = isDevelopment ? 'http://localhost:3000' : 'https://homecookt.com';
      
      console.log('Creating PayPal order with return URLs:', {
        return_url: `${currentDomain}/payment-success`,
        cancel_url: `${currentDomain}/payment-cancelled`
      });
      
      trackSubscription('payment_started', selectedPlan || 'unknown');
      
      // API call with return URLs included
      const response = await fetch('https://api.homecookt.com/api/v1/paypal/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          plan_type: currentPlan.planType, // WEEKLY, MONTHLY, or YEARLY
          return_url: `${currentDomain}/payment-success`, // Success return URL
          cancel_url: `${currentDomain}/payment-cancelled`, // Cancel return URL
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store the complete response data for success/cancel pages
        setPaypalOrderData(data);
        
        // Store order details using the API response structure
        localStorage.setItem('currentOrderPlan', selectedPlan || 'monthly');
        localStorage.setItem('currentOrderId', data.order_id); // PayPal order ID from API
        localStorage.setItem('currentOrderAmount', data.display_amount?.toString() || currentPlan.amount);
        localStorage.setItem('currentOrderCurrency', data.display_currency || currentPlan.currency);
        localStorage.setItem('currentChargedAmount', data.charged_amount?.toString() || currentPlan.amount);
        localStorage.setItem('currentChargedCurrency', data.charged_currency || currentPlan.currency);
        localStorage.setItem('currentOrderStatus', data.status || 'CREATED');
        localStorage.setItem('currentUserId', data.user_id?.toString() || '');
        
        console.log('PayPal order created successfully:', data);
        console.log('Redirecting to:', data.approval_link);
        
        // Redirect to PayPal approval URL
        window.location.href = data.approval_link;
      } else {
        // Handle API errors according to documentation
        if (response.status === 400) {
          if (data.message && data.message.toLowerCase().includes('active subscription')) {
            setError('You already have an active subscription. Please contact support if you need to change your plan.');
            setHasActiveSubscription(true);
            trackError('duplicate_subscription_api', 'payment_modal');
          } else {
            setError(data.message || 'Invalid plan type or subscription already exists.');
            trackError('paypal_order_validation', 'payment_modal');
          }
        } else if (response.status === 401) {
          setError('Authentication failed. Please log in again.');
          trackError('paypal_order_auth_failed', 'payment_modal');
        } else if (response.status === 404) {
          setError('Payment service not found. Please try again later.');
          trackError('paypal_order_not_found', 'payment_modal');
        } else if (response.status === 500) {
          setError('Server error occurred. Please try again later or contact support.');
          trackError('paypal_order_server_error', 'payment_modal');
        } else {
          setError(data.message || 'Failed to create PayPal order. Please try again.');
          trackError('paypal_order_unknown_error', 'payment_modal');
        }
      }
    } catch (error) {
      console.error('PayPal order creation error:', error);
      setError('Network error. Please check your connection and try again.');
      trackError('paypal_order_network_error', 'payment_modal');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStripeSuccess = () => {
    // Stripe will handle the redirect to success page
    // This callback is for any cleanup if needed
    onSuccess();
    trackSubscription('payment_completed', selectedPlan || 'unknown');
  };

  const handleBackToOptions = () => {
    setPaymentMethod('select');
    setError('');
  };

  // Handle PayPal return - redirect to appropriate pages
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const cancelled = urlParams.get('cancelled');
    
    if (cancelled === 'true') {
      // Payment was cancelled, redirect to cancelled page
      navigate('/payment-cancelled?reason=user_cancelled');
    } else if (token) {
      // Payment approved, redirect to success page for processing
      // The success page will use the stored order_id for capture
      navigate(`/payment-success?token=${token}`);
    }
  }, [navigate]);

  const handleBankTransferClick = () => {
    // Block bank transfer if user has active subscription
    if (hasActiveSubscription) {
      setError('You already have an active subscription. Please contact support to change your plan.');
      trackError('bank_transfer_blocked', 'payment_modal');
      return;
    }
    setShowBankTransfer(true);
  };

  const handleBackToPayPal = () => {
    setShowBankTransfer(false);
  };

  const handleViewBankDetails = () => {
    // Block bank transfer if user has active subscription
    if (hasActiveSubscription) {
      setError('You already have an active subscription. Please contact support to change your plan.');
      trackError('bank_transfer_blocked', 'payment_modal');
      return;
    }

    // Generate a bank transfer reference for bank payments
    const timestamp = Date.now().toString().slice(-8);
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const planCode = selectedPlan ? selectedPlan.charAt(0).toUpperCase() : 'M';
    const bankReference = `HC${planCode}${timestamp}${randomNum}`;
    
    // Store bank transfer details
    localStorage.setItem('currentOrderReference', bankReference);
    localStorage.setItem('currentOrderPlan', selectedPlan || 'monthly');
    localStorage.setItem('currentOrderAmount', currentPlan.amount);
    onClose();
    navigate('/payment-methods');
  };

  const handleContactSupport = () => {
    const message = `Hi, I have an active subscription and would like to inquire about changing my plan or managing my subscription.`;
    window.open(`https://wa.me/447737024736?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="payment-overlay">
      <div className="payment-modal">
        <button className="close-button9" onClick={onClose} disabled={isProcessing}>
          <X size={24} />
        </button>

        <h2 className="payment-title">Complete Payment</h2>
        
        <div className="plan-summary">
          <h3>
            Plan: {selectedPlan
              ? selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)
              : 'Unknown'} Subscription
          </h3>
          <p className="plan-price">
            {paypalOrderData ? (
              <>
                {paypalOrderData.display_currency} {paypalOrderData.display_amount}/{currentPlan.interval}
                {paypalOrderData.charged_currency !== paypalOrderData.display_currency && (
                  <span className="charged-amount">
                    (Charged: {paypalOrderData.charged_currency} {paypalOrderData.charged_amount})
                  </span>
                )}
              </>
            ) : (
              `${currentPlan.currency} £${currentPlan.amount}/${currentPlan.interval}`
            )}
          </p>
          {paypalOrderData && (
            <div className="order-id-display">
              <span className="order-label">Order ID:</span>
              <span className="order-number">{paypalOrderData.order_id}</span>
            </div>
          )}
        </div>

        {hasActiveSubscription ? (
          // Show active subscription notice instead of payment options
          <div className="active-subscription-notice">
            <h3>✅ Active Subscription Detected</h3>
            <p>You already have an active subscription. To change your plan or manage your subscription, please contact our support team.</p>
            <button onClick={handleContactSupport} className="contact-support-btn">
              📞 Contact Support
            </button>
          </div>
        ) : (
          <>
            {paymentMethod === 'select' && !showBankTransfer ? (
              <>
                <div className="paypal-section">
                  <button 
                    onClick={createPayPalOrder}
                    disabled={isProcessing || hasActiveSubscription}
                    className="paypal-button"
                  >
                    {isProcessing ? 'Creating Order...' : 'Pay with PayPal'}
                  </button>
                  
                  {isProcessing && (
                    <div className="processing-message">
                      {paypalOrderData ? 'Redirecting to PayPal...' : 'Creating your order...'}
                    </div>
                  )}
                </div>

                <div className="payment-divider">
                  <span>or</span>
                </div>

                <button 
                  className="stripe-button"
                  onClick={() => setPaymentMethod('stripe')}
                  disabled={isProcessing || hasActiveSubscription}
                >
                  <CreditCard size={20} />
                  Pay with Stripe
                </button>


                <button 
                  className="bank-transfer-btn"
                  onClick={handleBankTransferClick}
                  disabled={isProcessing || hasActiveSubscription}
                >
                  <CreditCard size={20} />
                  Secure Bank Transfer
                </button>
              </>
            ) : paymentMethod === 'stripe' ? (
              <StripePaymentForm 
                selectedPlan={selectedPlan || 'monthly'}
                stripePromise={stripePromise}
                onSuccess={handleStripeSuccess}
                onBack={handleBackToOptions}
              />
            ) : paymentMethod === 'paypal' ? (
              <div>
                <button onClick={handleBackToOptions} className="stripe-back-button">
                  <ArrowLeft size={16} />
                  Back to Payment Options
                </button>
              </div>
            ) : (
              <div className="bank-transfer-section">
                <div className="bank-transfer-header">
                  <button className="back-to-paypal" onClick={handleBackToPayPal}>
                    ← Back to PayPal
                  </button>
                  <h3>Secure Bank Transfer</h3>
                </div>
                
                <div className="bank-transfer-info">
                  <p>Complete your payment securely via bank transfer. You'll receive a reference number for your transfer on the next page.</p>
                  
                  <button 
                    className="view-details-btn"
                    onClick={handleViewBankDetails}
                    disabled={hasActiveSubscription}
                  >
                    <CreditCard size={20} />
                    View Bank Transfer Details
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="payment-security">
          <p>🔒 Your payment information is secure and encrypted</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;